import React from 'react';
import { PropsWithChildren } from 'react';
import { makeCSS } from '../../utils/makeCSS';
import { Thumbnail } from '../atoms/Thumbnail/Thumbnail';
import { SizeVariants } from '../../theme/themes';
import { Typography } from '../atoms/Typography';
import { Breadcrumbs, BreadValue } from '../atoms/Breadcrumbs';
import Truncate from '../utility/Truncate';

/**
 * The SearchItem is a visual indicator of the search results.
 * It displays visual variants for easy identification and information about their path.
 */

export interface SearchItemProps extends PropsWithChildren {
  /**Image that will be displayed in the Thumbnail */
  thumbnailSrc?: string;
  icon?: string;
  /** */
  title?: string;
  /**The path to the asset location */
  breadcrumbs?: BreadValue[] | string;
  active?: boolean;
}

const useCSS = makeCSS(({ theme }) => {
  return {
    root: {
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing[2],
      marginBottom: theme.spacing[1],
      marginRight: theme.spacing[4],
      width: '100%',
      minHeight: theme.spacing[10],
      height: theme.spacing[16],
      borderRadius: theme.borderRadius.lg,
      cursor: 'pointer',
      '&:hover': {
        background: theme.color.overlay.hovered
      },
      '&:active, &.active': {
        background: theme.color.overlay.pressed
      }
    },
    row: {
      flexDirection: 'row',
      display: 'inline',
      width: 297
    },
    highlight: {
      display: 'inline',
      width: 264
    }
  };
});

export const SearchItem = (props: SearchItemProps) => {
  const { thumbnailSrc, icon, title, active, breadcrumbs, children } = props;

  const classes = useCSS();

  return (
    <div className={`${classes.root} ${active ? 'active' : ''}`}>
      <Thumbnail
        size={SizeVariants.sm}
        content={icon ? 'file' : 'placeholder'}
        src={thumbnailSrc}
        fileIcon={icon}
      />
      <div className={`${classes.row}`}>
        <Typography variant="md" lineHeight="tight">
          <Truncate str={title} />
        </Typography>
        {typeof breadcrumbs === 'string' ? (
          <Typography colorVariant="03" variant="sm">
            {breadcrumbs}
          </Typography>
        ) : (
          <Breadcrumbs
            breadcrumbs={breadcrumbs}
            width={250}
            size={SizeVariants.sm}
            hideActiveState={true}
          />
        )}
      </div>
      <div className={`${classes.highlight}`}>{children}</div>
    </div>
  );
};
